/**
 * Ignite i18n
 *
 * Lightweight internationalisation engine — same API surface as react-i18next
 * so it can be swapped for the full library later with zero component changes.
 *
 *   const { t, lang, setLang } = useTranslation();
 *   t('auth.signIn')           → 'Sign In' | 'Iniciar sesión'
 *   t('auth.codeSentTo', email) → string built by the function value
 *
 * Language is stored in:
 *   1. localStorage ('ignite-lang')  — instant load on page refresh
 *   2. User's DB profile field       — synced on save (via ProfileScreen)
 *
 * Supported languages: 'en' (English) | 'es' (Spanish)
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import en from '../locales/en';
import es from '../locales/es';

// ── Registry ──────────────────────────────────────────────────────────────────
const LOCALES = { en, es };
export const SUPPORTED_LANGS = ['en', 'es'];

function detectInitialLang() {
  // 1. User explicitly chose a language
  const stored = localStorage.getItem('ignite-lang');
  if (stored && SUPPORTED_LANGS.includes(stored)) return stored;

  // 2. Browser preference
  const browser = (navigator.language || navigator.userLanguage || 'en').split('-')[0];
  return SUPPORTED_LANGS.includes(browser) ? browser : 'en';
}

// ── Context ───────────────────────────────────────────────────────────────────
const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);

  const setLang = useCallback((newLang) => {
    if (!SUPPORTED_LANGS.includes(newLang)) return;
    setLangState(newLang);
    localStorage.setItem('ignite-lang', newLang);
    // Update <html lang="…"> for accessibility
    document.documentElement.lang = newLang;
  }, []);

  // Set html lang on mount
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <I18nContext.Provider value={{ lang, setLang }}>
      {children}
    </I18nContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────
/**
 * useTranslation()
 *
 * Returns:
 *   t(key, ...args)  — translate a dot-separated key, optionally calling it as a function
 *   lang             — current language code ('en' | 'es')
 *   setLang(code)    — switch language
 *
 * Examples:
 *   t('auth.signIn')           → 'Sign In'
 *   t('auth.codeSentTo', email) → calls the function at that key with email
 *   t('nav.feed')              → 'Feed' | 'Inicio'
 */
export function useTranslation(namespace) {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useTranslation must be used inside <I18nProvider>');
  const { lang, setLang } = ctx;

  const t = useCallback((key, ...args) => {
    // The last arg may be an options object: { ns: 'otherNamespace' }.
    // When present, it overrides the hook's bound namespace for this call.
    // Documented in CLAUDE.md as: t('key', null, {ns:'other'})
    let nsOverride;
    if (args.length > 0) {
      const last = args[args.length - 1];
      if (last && typeof last === 'object' && !Array.isArray(last) && typeof last.ns === 'string') {
        nsOverride = last.ns;
        args = args.slice(0, -1);
      }
    }

    const effectiveNs = nsOverride !== undefined ? nsOverride : namespace;
    const fullKey = effectiveNs ? `${effectiveNs}.${key}` : key;
    const locale = LOCALES[lang] || LOCALES.en;
    const fallback = LOCALES.en;

    // Walk the key path
    const parts = fullKey.split('.');
    let value = locale;
    for (const part of parts) {
      value = value?.[part];
      if (value === undefined) break;
    }

    // Fall back to English if key missing in current locale
    if (value === undefined) {
      let fb = fallback;
      for (const part of parts) { fb = fb?.[part]; }
      value = fb;
    }

    if (value === undefined) {
      // Key not found anywhere — return the key itself as a last resort
      console.warn(`[i18n] Missing key: "${fullKey}" in lang "${lang}"`);
      return fullKey;
    }

    // If the value is a function (for interpolated strings), call it
    if (typeof value === 'function') {
      return value(...args);
    }

    return value;
  }, [lang, namespace]);

  return { t, lang, setLang };
}

// ── Utility: get language name in its own language ────────────────────────────
export function getLangLabel(code) {
  return LOCALES.en.languages?.[code] || code;
}
